import { createEffect, createSignal, onCleanup, untrack } from "solid-js"
import type { WatchGap, WatchedSession } from "../event-stream-watch"

type TrackedSession = { directory: string; sessionID: string; retained: boolean }

/**
 * Chooses the chats whose transcript events the event stream carries: those
 * on screen, and those the page holds in workspaces it follows, which it
 * keeps current. A watched chat the stream reports a gap for is stale; one
 * on screen reloads at once.
 */
export function createTranscriptWatchController(input: {
  tracked: () => readonly TrackedSession[]
  /** The workspaces the event stream follows (reactive). */
  covered: () => readonly string[]
  since: (directory: string, sessionID: string) => string | undefined
  watch: (sessions: readonly WatchedSession[]) => void
  onGaps: (listener: (gaps: WatchGap[]) => void) => () => void
  markStale: (directory: string, sessionIDs: string[], since?: string) => void
  currentSince: (directory: string, sessionID: string, since?: string) => boolean
  active: () => readonly { directory: string; sessionID: string }[]
  recover: (directory: string, sessionID: string) => void
}) {
  const [version, setVersion] = createSignal(0)
  let directories = new Map<string, string>()
  createEffect(() => {
    version()
    const covered = new Set(input.covered())
    untrack(() => {
      const sessions = input.tracked().filter((session) => session.retained || covered.has(session.directory))
      directories = new Map(sessions.map((session) => [session.sessionID, session.directory]))
      input.watch(sessions.map(({ directory, sessionID }) => ({ sessionID, since: () => input.since(directory, sessionID) })))
    })
  })
  onCleanup(input.onGaps((gaps) => {
    const active = new Set(input.active().map((target) => target.sessionID))
    for (const gap of gaps) {
      const directory = directories.get(gap.sessionID)
      if (!directory) continue
      // A snapshot requested after the newest lost event already holds it.
      input.markStale(directory, [gap.sessionID], gap.since)
      if (active.has(gap.sessionID) && !input.currentSince(directory, gap.sessionID, gap.since)) input.recover(directory, gap.sessionID)
    }
  }))
  return {
    /** The chats the page tracks changed. */
    changed: () => setVersion((value) => value + 1),
  }
}
